import { useCallback, useEffect, useMemo, useState } from 'react';
import axiosInstance from 'src/utils/axios';

const BASE = '/renter/appointments';

export interface RenterHost {
  id: number;
  name: string;
}

export interface RenterUnit {
  id: number;
  name: string;
  building_name?: string;
  label?: string;
}

export interface RenterAppointment {
  id: number;
  certification: number;
  property: number;
  property_name: string;
  category: number;
  category_name: string;
  start_at: string;
  end_at: string;
  start_local: string;
  duration_minutes?: number | null;
  location: string | null;
  status: 'scheduled' | 'rescheduled' | 'completed' | 'no_show' | 'cancelled';
  source: 'compliance' | 'renter_self' | 'agent';
  timezone: string;
  host: RenterHost | null;
  units?: RenterUnit[];
  can_cancel: boolean;
  can_reschedule: boolean;
  cancel_by: string;
  booking_instructions: string | null;
  rescheduled_from: number | null;
}

export interface RenterCategoryOption {
  id: number;
  name: string;
  description: string | null;
  duration_minutes: number;
  cancel_cutoff_hours: number;
  booking_instructions: string | null;
}

/** Why `bookable` is false — booking is by invitation (ENG-3895 follow-up). */
export type RenterBookingReason =
  | 'ok'
  | 'cert_closed'
  | 'no_invite'
  | 'invite_expired'
  | 'invite_used'
  | 'no_category'
  | 'no_host'
  | 'no_availability';

export interface RenterBookingInvite {
  id: number;
  state: 'active' | 'used' | 'revoked' | 'expired';
  expires_at: string | null;
  invited_at: string;
}

export interface RenterBookingOptions {
  certification: number;
  property: number;
  property_name: string;
  timezone: string;
  bookable: boolean;
  reason_code: RenterBookingReason;
  has_availability: boolean;
  duration_minutes?: number | null;
  invite: RenterBookingInvite | null;
  /** What the invite lets the renter book right now (empty when not bookable). */
  categories: RenterCategoryOption[];
  /** Property-level online-bookable types — drives rescheduling, which does
   * not need an invite. */
  renter_bookable_categories: RenterCategoryOption[];
  /** The existing appointment's type when the request names a reschedule. */
  reschedule_categories?: RenterCategoryOption[];
  hosts: RenterHost[];
  /** Homes the appointment can cover. Empty when the property has no units. */
  units?: RenterUnit[];
}

const formatDeadline = (iso: string | null | undefined): string =>
  iso
    ? new Date(iso).toLocaleDateString(undefined, {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric',
      })
    : '';

/** One readable unit label: `Building — 4B`, or the unit name on its own. */
export const renterUnitLabel = (unit: RenterUnit): string => {
  if (unit.label) {
    return unit.label;
  }
  return unit.building_name ? `${unit.building_name} — ${unit.name}` : unit.name;
};

/** Comma-joined labels for the first `max` units, plus how many were hidden. */
export const summarizeRenterUnits = (
  units: RenterUnit[] | undefined,
  max = 2
): { labels: string; extra: number } => {
  const all = (units ?? []).map(renterUnitLabel);
  return {
    labels: all.slice(0, max).join(', '),
    extra: Math.max(0, all.length - max),
  };
};

/** Renter-facing copy for a non-bookable options response. */
export const notBookableMessage = (
  options: RenterBookingOptions | null
): string => {
  const fallback =
    'Online booking is not open for this home right now. Please call the office to set up a time.';
  if (!options) {
    return fallback;
  }
  switch (options.reason_code) {
    case 'no_invite':
      return 'Online booking is by invitation. Your property team will let you know when you can book a time, or call the office.';
    case 'invite_expired':
      return `Your invitation to book online expired on ${formatDeadline(
        options.invite?.expires_at
      )}. Please call the office to set up a time.`;
    case 'invite_used':
      return 'You already booked your appointment. You can change or cancel it from your appointments.';
    case 'no_availability':
      return 'There are no open times right now. Please check back soon or call the office.';
    default:
      return fallback;
  }
};

export interface RenterSlot {
  start_at: string;
  end_at: string;
  host_id: number;
  host_name: string;
}

export interface RenterSlotDay {
  date: string;
  slots: RenterSlot[];
}

export interface RenterSlotsResponse {
  certification: number;
  category: number;
  timezone: string;
  start: string;
  end: string;
  days: RenterSlotDay[];
}

export interface RenterApiError {
  code: string;
  detail: string;
  status: number;
}

export const toRenterApiError = (err: unknown): RenterApiError => {
  const response = (
    err as { response?: { status?: number; data?: Record<string, unknown> } }
  )?.response;
  const data = response?.data ?? {};
  return {
    status: response?.status ?? 0,
    code: typeof data.code === 'string' ? data.code : 'unknown',
    detail:
      typeof data.detail === 'string'
        ? data.detail
        : 'Could not complete the request. Please try again.',
  };
};

export async function fetchRenterBookingOptions(
  certificationId: number,
  rescheduleAppointmentId?: number | null
): Promise<RenterBookingOptions | null> {
  try {
    const search = new URLSearchParams({
      certification: String(certificationId),
    });
    if (rescheduleAppointmentId) {
      search.set('reschedule', String(rescheduleAppointmentId));
    }
    const response = await axiosInstance.get(
      `${BASE}/options/?${search.toString()}`
    );
    return response.data as RenterBookingOptions;
  } catch (err: unknown) {
    const status = (err as { response?: { status?: number } })?.response
      ?.status;
    // 404 = feature off for this property (or not a member) — not an error to surface.
    if (status === 404) {
      return null;
    }
    throw err;
  }
}

export async function fetchRenterSlots(params: {
  certificationId: number;
  categoryId: number;
  start: string;
  end: string;
  excludeAppointmentId?: number | null;
  rescheduleAppointmentId?: number | null;
}): Promise<RenterSlotsResponse> {
  // Renters never filter by host: the backend merges every host's slots and
  // each slot carries the host who can take it.
  const search = new URLSearchParams({
    certification: String(params.certificationId),
    category: String(params.categoryId),
    start: params.start,
    end: params.end,
  });
  if (params.excludeAppointmentId) {
    search.set('exclude', String(params.excludeAppointmentId));
  }
  if (params.rescheduleAppointmentId) {
    search.set('reschedule', String(params.rescheduleAppointmentId));
  }
  const response = await axiosInstance.get(
    `${BASE}/slots/?${search.toString()}`
  );
  return response.data as RenterSlotsResponse;
}

export async function bookRenterAppointment(payload: {
  certification: number;
  category: number;
  start_at: string;
}): Promise<RenterAppointment> {
  // The server resolves the host from the chosen slot — never send one.
  const response = await axiosInstance.post(`${BASE}/book/`, payload);
  return response.data as RenterAppointment;
}

export async function cancelRenterAppointment(
  id: number
): Promise<RenterAppointment> {
  const response = await axiosInstance.post(`${BASE}/${id}/cancel/`);
  return response.data as RenterAppointment;
}

export async function rescheduleRenterAppointment(
  id: number,
  payload: { new_start_at: string }
): Promise<RenterAppointment> {
  const response = await axiosInstance.post(
    `${BASE}/${id}/reschedule/`,
    payload
  );
  return response.data as RenterAppointment;
}

/** Event types the backend broadcasts to the certification channel when an
 * appointment is created, moved, or cancelled. */
const APPOINTMENT_WS_EVENTS = new Set([
  'appointment_created',
  'appointment_updated',
  'appointment_cancelled',
]);

interface UseRenterAppointmentsResult {
  upcoming: RenterAppointment[];
  past: RenterAppointment[];
  loading: boolean;
  error: string | null;
  unavailable: boolean;
  refresh: () => Promise<void>;
}

/** The renter's appointments across all their active certs. `unavailable`
 * is true when the feature is off for every property (404) — callers hide
 * the surface instead of showing an error. */
export function useRenterAppointments(
  enabled = true
): UseRenterAppointmentsResult {
  const [upcoming, setUpcoming] = useState<RenterAppointment[]>([]);
  const [past, setPast] = useState<RenterAppointment[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [unavailable, setUnavailable] = useState(false);

  const refresh = useCallback(async (): Promise<void> => {
    if (!enabled) {
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const response = await axiosInstance.get(`${BASE}/`);
      setUpcoming(response.data.upcoming ?? []);
      setPast(response.data.past ?? []);
      setUnavailable(false);
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response
        ?.status;
      if (status === 404 || status === 403) {
        setUnavailable(true);
        setUpcoming([]);
        setPast([]);
      } else {
        setError('We could not load your appointments. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }, [enabled]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    // Appointment mutations arrive on the certification WebSocket, which the
    // renter layout re-dispatches as `CertificationWebSocketEvent` with the
    // event type in `detail.type`. Listening for bare `appointment_*` events
    // never fired, so the renter view stayed stale after a cancellation
    // (ENG-3962).
    const handler = (event: Event): void => {
      const type = (event as CustomEvent<{ type?: string }>).detail?.type;
      if (type && APPOINTMENT_WS_EVENTS.has(type)) {
        refresh();
      }
    };
    document.addEventListener('CertificationWebSocketEvent', handler);
    return () => {
      document.removeEventListener('CertificationWebSocketEvent', handler);
    };
  }, [refresh]);

  return useMemo(
    () => ({ upcoming, past, loading, error, unavailable, refresh }),
    [upcoming, past, loading, error, unavailable, refresh]
  );
}
